
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Facebook, Twitter, Instagram, Youtube, Clipboard, Check, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { shareToSocialMedia } from '@/utils/socialSharingService';
import { supabase } from '@/lib/supabase';

export interface SocialSharePanelProps {
  isOpen?: boolean;
  onClose?: () => void;
  videoId?: number | undefined;
  userId?: string | null;
  videoUrl?: string | undefined;
  url?: string;
  title?: string;
  description?: string;
  merchantId?: string;
  eventId?: number | null;
}

const SocialSharePanel: React.FC<SocialSharePanelProps> = ({ 
  isOpen, 
  onClose, 
  videoId, 
  userId, 
  videoUrl,
  url,
  title: propTitle,
  description: propDescription,
  merchantId,
  eventId
}) => {
  const [isSharing, setIsSharing] = useState(false);
  const [sharedPlatforms, setSharedPlatforms] = useState<string[]>([]);

  // Get current user if not provided
  const getCurrentUserId = async (): Promise<string | null> => {
    if (userId) return userId;
    
    const { data } = await supabase.auth.getSession();
    return data.session?.user?.id || null;
  };
  
  const handleShare = async (platform: string, shareUrl: string) => {
    try {
      setIsSharing(true);
      
      // Get current user ID if not provided
      const currentUserId = await getCurrentUserId();
      
      if (!currentUserId) {
        toast.error('You need to be logged in to share content');
        return;
      }

      console.log(`Sharing to ${platform}`, {
        url: shareUrl,
        title: title,
        userId: currentUserId,
        merchantId: merchantId
      });
      
      // Share to the selected platform
      const success = await shareToSocialMedia(
        platform,
        shareUrl,
        title,
        description,
        currentUserId,
        merchantId,
        eventId
      );
      
      if (success) {
        toast.success(`Shared on ${platform}!`);
        setSharedPlatforms([...sharedPlatforms, platform]);
        
        // Close the panel after a short delay
        setTimeout(() => {
          if (onClose) onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share content');
    } finally {
      setIsSharing(false);
    }
  };

  // Handle sharing to all platforms at once
  const handleShareAll = async () => {
    const platforms = ['Facebook', 'Twitter', 'Instagram', 'YouTube'];
    let successCount = 0;
    
    setIsSharing(true);
    
    for (const platform of platforms) {
      if (!sharedPlatforms.includes(platform)) {
        try {
          await handleShare(platform, shareUrl);
          successCount++;
        } catch (error) {
          console.error(`Error sharing to ${platform}:`, error);
        }
      }
    }
    
    setIsSharing(false);
    
    if (successCount > 0) {
      toast.success(`Shared to ${successCount} platform${successCount > 1 ? 's' : ''}!`);
    } else {
      toast.error("Failed to share to any platforms");
    }
  };
  
  const title = propTitle || "Check out this awesome video!";
  const description = propDescription || "I recorded this on GrinGauge. Take a look!";
  const shareUrl = url || videoUrl || window.location.href;
  
  // For standalone usage with Dialog
  if (isOpen !== undefined && onClose) {
    return (
      <Dialog open={isOpen} onOpenChange={() => onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share This Video</DialogTitle>
            <DialogDescription>
              Choose a platform to share this video
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <Button 
              onClick={() => handleShare('Facebook', shareUrl)}
              className="w-full bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center"
              disabled={isSharing || sharedPlatforms.includes('Facebook')}
            >
              {sharedPlatforms.includes('Facebook') ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Facebook className="h-4 w-4 mr-2" />
              )}
              Share to Facebook
            </Button>
            
            <Button 
              onClick={() => handleShare('Twitter', shareUrl)}
              className="w-full bg-blue-400 text-white hover:bg-blue-500 flex items-center justify-center"
              disabled={isSharing || sharedPlatforms.includes('Twitter')}
            >
              {sharedPlatforms.includes('Twitter') ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Twitter className="h-4 w-4 mr-2" />
              )}
              Share to Twitter
            </Button>
            
            <Button 
              onClick={() => handleShare('Instagram', shareUrl)}
              className="w-full bg-pink-600 text-white hover:bg-pink-700 flex items-center justify-center"
              disabled={isSharing || sharedPlatforms.includes('Instagram')}
            >
              {sharedPlatforms.includes('Instagram') ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Instagram className="h-4 w-4 mr-2" />
              )}
              Share to Instagram
            </Button>
            
            <Button 
              onClick={() => handleShare('YouTube', shareUrl)}
              className="w-full bg-red-600 text-white hover:bg-red-700 flex items-center justify-center"
              disabled={isSharing || sharedPlatforms.includes('YouTube')}
            >
              {sharedPlatforms.includes('YouTube') ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Youtube className="h-4 w-4 mr-2" />
              )}
              Share to YouTube
            </Button>
            
            <Button 
              onClick={() => handleShare('Clipboard', shareUrl)}
              className="w-full bg-gray-600 text-white hover:bg-gray-700 flex items-center justify-center"
              disabled={isSharing || sharedPlatforms.includes('Clipboard')}
            >
              {sharedPlatforms.includes('Clipboard') ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Clipboard className="h-4 w-4 mr-2" />
              )}
              Copy Link
            </Button>
            
            <Button 
              onClick={handleShareAll}
              className="w-full mt-6 bg-gradient-to-r from-blue-600 via-pink-500 to-red-600 text-white flex items-center justify-center"
              disabled={isSharing}
            >
              {isSharing ? (
                <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></div>
              ) : (
                <Share2 className="h-4 w-4 mr-2" />
              )}
              Share to All Platforms
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  // For inline usage
  return (
    <div className="bg-black/80 rounded-lg p-4 shadow-lg">
      <h3 className="text-white text-sm mb-2">Share to:</h3>
      <div className="flex gap-2 flex-wrap">
        <Button 
          onClick={() => handleShare('Facebook', shareUrl)}
          className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center"
          disabled={isSharing || sharedPlatforms.includes('Facebook')}
        >
          {sharedPlatforms.includes('Facebook') ? <Check className="h-3 w-3 mr-1" /> : <Facebook className="h-3 w-3 mr-1" />}
          Facebook
        </Button>
        
        <Button 
          onClick={() => handleShare('Twitter', shareUrl)}
          className="px-2 py-1 bg-blue-400 text-white text-xs rounded hover:bg-blue-500 flex items-center"
          disabled={isSharing || sharedPlatforms.includes('Twitter')}
        >
          {sharedPlatforms.includes('Twitter') ? <Check className="h-3 w-3 mr-1" /> : <Twitter className="h-3 w-3 mr-1" />}
          Twitter
        </Button>
        
        <Button 
          onClick={() => handleShare('Instagram', shareUrl)}
          className="px-2 py-1 bg-pink-600 text-white text-xs rounded hover:bg-pink-700 flex items-center"
          disabled={isSharing || sharedPlatforms.includes('Instagram')}
        >
          {sharedPlatforms.includes('Instagram') ? <Check className="h-3 w-3 mr-1" /> : <Instagram className="h-3 w-3 mr-1" />}
          Instagram
        </Button>
        
        <Button 
          onClick={() => handleShare('YouTube', shareUrl)}
          className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center"
          disabled={isSharing || sharedPlatforms.includes('YouTube')}
        >
          {sharedPlatforms.includes('YouTube') ? <Check className="h-3 w-3 mr-1" /> : <Youtube className="h-3 w-3 mr-1" />}
          YouTube
        </Button>
        
        <Button 
          onClick={() => handleShare('Clipboard', shareUrl)}
          className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 flex items-center"
          disabled={isSharing || sharedPlatforms.includes('Clipboard')}
        >
          {sharedPlatforms.includes('Clipboard') ? <Check className="h-3 w-3 mr-1" /> : <Clipboard className="h-3 w-3 mr-1" />}
          Copy Link
        </Button>
      </div>
      
      <Button 
        onClick={handleShareAll}
        className="w-full mt-3 bg-gradient-to-r from-blue-600 via-pink-500 to-red-600 text-white text-xs hover:opacity-90 flex items-center justify-center"
        disabled={isSharing}
      >
        {isSharing ? (
          <div className="animate-spin mr-2 h-3 w-3 border-t-2 border-b-2 border-white rounded-full"></div>
        ) : (
          <Share2 className="h-3 w-3 mr-1" />
        )}
        Share to All Platforms
      </Button>
    </div>
  );
};

export default SocialSharePanel;
